import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, useLocation, Link } from 'react-router-dom';
import axios from 'axios';
import { useCart } from '../contexts/CartContext';

import { API_BASE_URL } from '../config/api';

function CheckoutSuccessPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get('orderId');
  const { clearCart } = useCart();
  const [orderDetails, setOrderDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [shippingConfig, setShippingConfig] = useState({
    freeShippingThreshold: 100.00,
    freeShippingMessage: "FREE Shipping on orders over $100.00",
    freeShippingSuccessMessage: "You got FREE Shipping!"
  });

  useEffect(() => {
    const processOrder = async () => {
      setLoading(true);
      setError(null);
      
      try {
        // Try to get order details from navigation state first
        if (location.state) {
          console.log('Using order details from location.state');
          setOrderDetails(location.state);
          setLoading(false);
          return;
        }

        // Check if this is a redirect from Amazon Pay or other redirect-based payment
        const paymentIntentId = searchParams.get('payment_intent');
        const redirectStatus = searchParams.get('redirect_status');
        
        console.log('CheckoutSuccessPage - Checking for redirect payment:', {
          paymentIntentId,
          redirectStatus,
          orderId,
          hasLocationState: !!location.state
        });
        
        // Handle failed payment redirects - prevent order processing and redirect to checkout
        if (paymentIntentId && redirectStatus && redirectStatus !== 'succeeded' && !orderId) {
          console.error('Payment failed or was canceled:', { paymentIntentId, redirectStatus });
          
          // Get order data to restore cart items
          const savedOrderData = localStorage.getItem('checkoutOrderData');
          if (savedOrderData) {
            try {
              const orderData = JSON.parse(savedOrderData);
              // Restore cart items from orderData
              if (orderData.items && orderData.items.length > 0) {
                localStorage.setItem('cart', JSON.stringify(orderData.items));
              }
            } catch (err) {
              console.error('Error parsing checkoutOrderData for cart restoration:', err);
            }
          }
          
          // Determine error message
          let errorMessage = 'Payment failed';
          if (redirectStatus === 'failed') {
            errorMessage = 'Payment failed';
          } else if (redirectStatus === 'canceled') {
            errorMessage = 'Payment was canceled';
          } else {
            errorMessage = `Payment ${redirectStatus}`;
          }
          
          // Redirect to checkout with error message
          navigate(`/checkout?payment_error=${encodeURIComponent(errorMessage)}`, { 
            replace: true,
            state: { 
              paymentFailed: true,
              errorMessage: `${errorMessage}. Your order was not placed. Please try again.`
            }
          });
          return;
        }
        
        if (paymentIntentId && redirectStatus === 'succeeded' && !orderId) {
          // This is a redirect from Amazon Pay or similar - need to confirm payment
          console.log('Processing redirect-based payment confirmation...', { paymentIntentId });
          try {
            // Get order data from localStorage (should have been saved before redirect)
            const savedOrderData = localStorage.getItem('checkoutOrderData');
            if (!savedOrderData) {
              console.error('No order data found in localStorage for redirect payment');
              // Try to get from lastOrder as fallback
              const lastOrder = localStorage.getItem('lastOrder');
              if (lastOrder) {
                try {
                  const order = JSON.parse(lastOrder);
                  console.log('Using lastOrder as fallback');
                  setOrderDetails(order);
                  setLoading(false);
                  return;
                } catch (err) {
                  console.error('Error parsing lastOrder:', err);
                }
              }
              setError('Order data not found. Please contact support if payment was charged.');
              setLoading(false);
              return;
            }

            const orderData = JSON.parse(savedOrderData);
            console.log('Order data retrieved from localStorage:', {
              itemsCount: orderData.items?.length || 0,
              customerEmail: orderData.email || orderData.shippingInfo?.email || 'N/A'
            });
            
            // Confirm payment with backend
            console.log('Calling /payment/confirm endpoint...', {
              paymentIntentId,
              hasOrderData: !!orderData
            });
            
            // Include admin token if logged in (for test mode)
            const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
            const headers = token ? { Authorization: `Bearer ${token}` } : {};
            
            const response = await axios.post(`${API_BASE_URL}/payment/confirm`, {
              paymentIntentId: paymentIntentId,
              orderData: orderData
            }, { headers });

            console.log('Backend confirmation response:', response.data);

            if (response.data.success && response.data.orderId) {
              // Build order details from the response and saved data
              const orderDetails = {
                orderId: response.data.orderId,
                items: orderData.items || [],
                shippingInfo: orderData.shippingInfo || orderData,
                subtotal: orderData.subtotal || 0,
                shipping: orderData.shipping || 0,
                total: orderData.total || 0,
                date: new Date().toISOString(),
                freeShipping: orderData.freeShipping || false,
                isDigitalOnly: orderData.isDigitalOnly || false
              };
              
              console.log('Order confirmed successfully:', { orderId: response.data.orderId });
              
              // Save to localStorage for future reference
              localStorage.setItem('lastOrder', JSON.stringify(orderDetails));
              
              // Clear the cart since payment succeeded
              clearCart();
              
              // Clean up temporary checkout data
              localStorage.removeItem('checkoutOrderData');
              
              // Update URL with orderId
              const newUrl = new URL(window.location);
              newUrl.searchParams.set('orderId', response.data.orderId);
              newUrl.searchParams.delete('payment_intent');
              newUrl.searchParams.delete('payment_intent_client_secret');
              newUrl.searchParams.delete('redirect_status');
              window.history.replaceState({}, '', newUrl);
              
              setOrderDetails(orderDetails);
              setLoading(false);
            } else {
              console.error('Failed to confirm payment:', response.data);
              setError('Failed to confirm payment. Please contact support if payment was charged.');
              setLoading(false);
            }
          } catch (err) {
            console.error('Error processing redirect payment:', err);
            console.error('Error details:', {
              message: err.message,
              response: err.response?.data,
              status: err.response?.status,
              stack: err.stack
            });
            
            // Get order data to restore cart items
            const savedOrderData = localStorage.getItem('checkoutOrderData');
            if (savedOrderData) {
              try {
                const orderData = JSON.parse(savedOrderData);
                // Restore cart items from orderData
                if (orderData.items && orderData.items.length > 0) {
                  localStorage.setItem('cart', JSON.stringify(orderData.items));
                }
              } catch (parseErr) {
                console.error('Error parsing checkoutOrderData for cart restoration:', parseErr);
              }
            }
            
            // Check if the error is because payment status is not succeeded
            const errorMessage = err.response?.data?.error || err.message || 'Unknown error';
            let userMessage = 'Payment was not completed successfully. Your order was not placed. Please try again.';
            if (errorMessage.includes('Payment not completed') || errorMessage.includes('status')) {
              userMessage = 'Payment was not completed successfully. Your order was not placed. Please try again or contact support if you were charged.';
            }
            
            // Redirect to checkout with error message
            navigate(`/checkout?payment_error=${encodeURIComponent(userMessage)}`, { 
              replace: true,
              state: { 
                paymentFailed: true,
                errorMessage: userMessage
              }
            });
            return;
          }
          return;
        }

        // Normal flow: get order details from localStorage
        const savedOrder = localStorage.getItem('lastOrder');
        if (savedOrder) {
          try {
            const order = JSON.parse(savedOrder);
            // Match by orderId if provided, otherwise use the saved order
            // If orderId is in URL but doesn't match, still show the saved order
            // (might be a timing issue or the URL hasn't been updated yet)
            if (!orderId || order.orderId === orderId) {
              console.log('Using order details from localStorage', { 
                orderId: order.orderId, 
                urlOrderId: orderId,
                match: order.orderId === orderId 
              });
              setOrderDetails(order);
              setLoading(false);
              return;
            } else {
              // OrderId in URL doesn't match, but show the saved order anyway
              // (it's likely the most recent order and the URL might be outdated)
              console.warn('OrderId mismatch, but showing saved order anyway:', { 
                savedOrderId: order.orderId, 
                urlOrderId: orderId 
              });
              setOrderDetails(order);
              setLoading(false);
              return;
            }
          } catch (err) {
            console.error('Error parsing saved order:', err);
          }
        }

        // If we have an orderId but no matching data, show error
        if (orderId) {
          console.warn('OrderId in URL but no order data found in localStorage', { orderId });
          setError(`Order ${orderId} not found. If you just completed payment, please wait a moment and refresh the page. Otherwise, please check your email for order confirmation.`);
        } else {
          setError('No order information found. Please check your email for order confirmation.');
        }
        setLoading(false);
      } catch (err) {
        console.error('Unexpected error in processOrder:', err);
        setError('An error occurred while loading order details. Please contact support.');
        setLoading(false);
      }
    };

    processOrder();

    // Fetch shipping config
    const fetchConfig = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/config`);
        if (response.data.success && response.data.data.shipping) {
          setShippingConfig(response.data.data.shipping);
        }
      } catch (err) {
        console.error('Error loading shipping config:', err);
      }
    };
    fetchConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- clearCart, navigate are stable from context
  }, [location.state, orderId, searchParams]);

  if (loading) {
    return (
      <div className="min-h-screen py-8 px-4 bg-gray-50">
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-lg shadow-md p-8 text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-4"></div>
            <p className="text-gray-600">Loading order details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen py-8 px-4 bg-gray-50">
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-lg shadow-md p-8 text-center">
            <svg
              className="w-16 h-16 mx-auto text-yellow-600 mb-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Unable to Load Order Details</h1>
            <p className="text-gray-600 mb-6">{error}</p>
            {orderId && (
              <p className="text-sm text-gray-500 mb-6">
                Order ID: <span className="font-mono font-semibold text-gray-900">{orderId}</span>
              </p>
            )}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => window.location.reload()}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
              >
                Refresh Page
              </button>
              <Link
                to="/"
                className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition-colors text-center"
              >
                Continue Shopping
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!orderDetails) {
    return (
      <div className="min-h-screen py-8 px-4 bg-gray-50">
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-lg shadow-md p-8 text-center">
            <p className="text-gray-600">No order details available.</p>
            <Link
              to="/"
              className="mt-4 inline-block px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
            >
              Continue Shopping
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const { items, shippingInfo, subtotal, shipping, total: orderTotal, freeShipping, isDigitalOnly } = orderDetails;
  
  // Check if order is digital-only (fallback check if isDigitalOnly not set)
  const isDigital = isDigitalOnly !== undefined ? isDigitalOnly : (items && items.length > 0 && items.every(item => item.type === 'Digital'));

  return (
    <div className="min-h-screen py-8 px-4 bg-gray-50">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-lg shadow-md p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <svg
              className="w-20 h-20 mx-auto text-green-600 mb-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Order Confirmed!</h1>
            <p className="text-lg text-gray-600 mb-4">Thank you for your purchase!</p>
            {orderId && (
              <p className="text-sm text-gray-500">
                Order ID: <span className="font-mono font-semibold text-gray-900">{orderId}</span>
              </p>
            )}
            <p className="text-sm text-gray-500 mt-2">
              {new Date(orderDetails.date).toLocaleString()}
            </p>
          </div>

          {/* Order Items */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 border-b border-gray-200 pb-2">
              Order Items
            </h2>
            <div className="space-y-4">
              {items.map((item, index) => (
                <div
                  key={index}
                  className="flex items-start gap-4 pb-4 border-b border-gray-200 last:border-0"
                >
                  <img
                    src={item.imageUrl}
                    alt={item.designTitle}
                    className="w-20 h-20 object-contain rounded border border-gray-200"
                  />
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900">{item.designTitle}</h3>
                    <p className="text-sm text-gray-600">{item.formatName}</p>
                    <div className="mt-2 flex items-center gap-4 text-sm">
                      <span className="text-gray-600">Quantity: <span className="font-semibold text-gray-900">{item.quantity}</span></span>
                      <span className="text-gray-600">Price: <span className="font-semibold text-gray-900">${item.price.toFixed(2)}</span></span>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-gray-900 text-lg">
                      ${(item.price * item.quantity).toFixed(2)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Shipping Information / Customer Information */}
          {shippingInfo && (
            <div className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4 border-b border-gray-200 pb-2">
                {isDigital ? 'Customer Information' : 'Shipping Information'}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-600">Name:</p>
                  <p className="font-semibold text-gray-900">{shippingInfo.name}</p>
                </div>
                <div>
                  <p className="text-gray-600">Email:</p>
                  <p className="font-semibold text-gray-900">{shippingInfo.email}</p>
                </div>
                <div>
                  <p className="text-gray-600">Phone:</p>
                  <p className="font-semibold text-gray-900">{shippingInfo.phone}</p>
                </div>
                <div>
                  <p className="text-gray-600">Address:</p>
                  <p className="font-semibold text-gray-900">
                    {shippingInfo.address}<br />
                    {shippingInfo.city}, {shippingInfo.state} {shippingInfo.zip}<br />
                    {shippingInfo.country}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Order Summary */}
          <div className="bg-gray-50 rounded-lg p-6 mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Order Summary</h2>
            <div className="space-y-2">
              <div className="flex justify-between text-gray-600">
                <span>Subtotal:</span>
                <span className="font-semibold text-gray-900">${subtotal.toFixed(2)}</span>
              </div>
              {isDigital ? (
                <div className="flex justify-between text-gray-600">
                  <span>Instant Download:</span>
                  <span className="font-semibold text-green-600">FREE</span>
                </div>
              ) : (
                <>
                  <div className="flex justify-between text-gray-600">
                    <span>Shipping:</span>
                    <span className={`font-semibold ${freeShipping ? 'text-green-600' : 'text-gray-900'}`}>
                      {freeShipping ? 'FREE' : `$${shipping.toFixed(2)}`}
                    </span>
                  </div>
                  {freeShipping && (
                    <p className="text-sm text-green-600 font-semibold text-center mt-2">
                      {shippingConfig.freeShippingSuccessMessage || "You got FREE Shipping!"}
                    </p>
                  )}
                </>
              )}
              <div className="border-t border-gray-300 pt-2 mt-2">
                <div className="flex justify-between text-lg font-bold text-gray-900">
                  <span>Total:</span>
                  <span>${orderTotal.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* What's Next */}
          <div className="bg-blue-50 rounded-lg p-6 mb-8">
            <h2 className="font-semibold text-gray-900 mb-3">What's Next?</h2>
            <ul className="space-y-2 text-sm text-gray-600">
              <li className="flex items-start gap-2">
                <svg className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span>You will receive an email confirmation shortly</span>
              </li>
              <li className="flex items-start gap-2">
                <svg className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span>Your order is being processed and will be fulfilled soon</span>
              </li>
              {isDigital && (
                <li className="flex items-start gap-2">
                  <svg className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Download links will be sent to your email when ready</span>
                </li>
              )}
            </ul>
          </div>
          
          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/"
              className="px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors text-center"
            >
              Continue Shopping
            </Link>
            <button
              onClick={() => window.print()}
              className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition-colors"
            >
              Print Receipt
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CheckoutSuccessPage;

